/**
 * V70G7B — 공지 작성/수정 폼의 팝업 설정 영역.
 * 날짜는 KST 기준 'YYYY-MM-DD'. 시작일 00:00 KST 부터, 종료일 23:59:59 KST 까지 표시.
 */
import React from 'react';
import {
  PopupDismissPolicy,
  POPUP_DISMISS_POLICY_OPTIONS,
  DEFAULT_POPUP_PRIORITY,
} from '../../shared/hooks/useAnnouncements';

export interface PopupFormState {
  isPopup: boolean;
  startDate: string;
  endDate: string;
  dismissPolicy: PopupDismissPolicy;
  priority: string;
  bumpRevision: boolean;
}

export const INITIAL_POPUP_FORM_STATE: PopupFormState = {
  isPopup: false,
  startDate: '',
  endDate: '',
  dismissPolicy: 'onceDismissed',
  priority: String(DEFAULT_POPUP_PRIORITY),
  bumpRevision: false,
};

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

/** 'YYYY-MM-DD' (KST) → 해당 날짜 00:00 KST 의 ms epoch */
export function kstDateToMs(ymd: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - KST_OFFSET_MS;
}

/** ISO → 'YYYY-MM-DD' (KST) */
export function isoToKstDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms + KST_OFFSET_MS).toISOString().slice(0, 10);
}

/** 폼 검증 — 오류 메시지 또는 null */
export function validatePopupForm(s: PopupFormState): string | null {
  if (!s.isPopup) return null;
  const pr = Number(s.priority);
  if (s.priority.trim() === '' || !Number.isInteger(pr) || pr < 0 || pr > 10000) {
    return '팝업 우선순위는 0~10000 사이의 정수로 입력해주세요.';
  }
  const start = s.startDate ? kstDateToMs(s.startDate) : null;
  const end = s.endDate ? kstDateToMs(s.endDate) : null;
  if (s.startDate && start === null) return '팝업 시작일 형식이 올바르지 않습니다.';
  if (s.endDate && end === null) return '팝업 종료일 형식이 올바르지 않습니다.';
  if (start !== null && end !== null && end < start) return '팝업 종료일은 시작일 이후여야 합니다.';
  return null;
}

/**
 * 요청 payload 의 팝업 필드.
 * wasPopup=false 이고 지금도 팝업이 아니면 필드를 보내지 않는다 (기존 공지 영향 없음).
 */
export function buildPopupPayload(s: PopupFormState, wasPopup: boolean, isEditMode: boolean) {
  if (!s.isPopup) return wasPopup ? { isPopup: false } : {};
  return {
    isPopup: true,
    popupStartAt: s.startDate ? kstDateToMs(s.startDate) : null,
    popupEndAt: s.endDate ? kstDateToMs(s.endDate) : null,
    popupDismissPolicy: s.dismissPolicy,
    popupPriority: Number(s.priority),
    ...(isEditMode && wasPopup && s.bumpRevision ? { popupBumpRevision: true } : {}),
  };
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--color-gray-700)', marginBottom: '6px',
};
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--color-gray-300)', borderRadius: '8px', fontSize: '14px',
  width: '100%', boxSizing: 'border-box',
};
const hintStyle: React.CSSProperties = { marginTop: '6px', fontSize: '12px', color: 'var(--color-gray-500)' };

interface Props {
  value: PopupFormState;
  onChange: (next: PopupFormState) => void;
  isEditMode: boolean;
  wasPopup: boolean;
}

export function PopupSettingsSection({ value, onChange, isEditMode, wasPopup }: Props) {
  const set = <K extends keyof PopupFormState>(k: K, v: PopupFormState[K]) => onChange({ ...value, [k]: v });
  const selectedPolicy = POPUP_DISMISS_POLICY_OPTIONS.find((o) => o.value === value.dismissPolicy);

  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: 'var(--color-gray-700)' }}>
        <input
          type="checkbox"
          checked={value.isPopup}
          onChange={(e) => set('isPopup', e.target.checked)}
          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        />
        팝업으로 표시
      </label>
      <div style={hintStyle}>로그인 후 ERP 화면에 팝업으로 공지를 표시합니다. 표시 대상(국가/권한)은 공지 열람 권한과 동일합니다.</div>

      {value.isPopup && (
        <div style={{ marginTop: '12px', padding: '16px', border: '1px solid var(--color-gray-200)', borderRadius: '10px', background: 'var(--color-gray-50)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div>
              <span style={labelStyle}>표시 시작일 (KST)</span>
              <input type="date" value={value.startDate} onChange={(e) => set('startDate', e.target.value)} style={inputStyle} aria-label="팝업 표시 시작일" />
            </div>
            <div>
              <span style={labelStyle}>표시 종료일 (KST)</span>
              <input type="date" value={value.endDate} min={value.startDate || undefined} onChange={(e) => set('endDate', e.target.value)} style={inputStyle} aria-label="팝업 표시 종료일" />
            </div>
            <div>
              <span style={labelStyle}>우선순위</span>
              <input type="number" min={0} max={10000} step={1} value={value.priority} onChange={(e) => set('priority', e.target.value)} style={inputStyle} aria-label="팝업 우선순위" />
            </div>
          </div>
          <div style={hintStyle}>
            비워두면 기간 제한 없이 표시합니다. 종료일은 해당 날짜 23:59(KST)까지 포함합니다. 우선순위는 숫자가 낮을수록 먼저 표시되며(기본 {DEFAULT_POPUP_PRIORITY}), 같으면 최신 공지가 먼저 표시됩니다.
          </div>

          <div style={{ marginTop: '14px' }}>
            <span style={labelStyle}>닫기 정책</span>
            <select
              value={value.dismissPolicy}
              onChange={(e) => set('dismissPolicy', e.target.value as PopupDismissPolicy)}
              style={inputStyle}
              aria-label="팝업 닫기 정책"
            >
              {POPUP_DISMISS_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {selectedPolicy && <div style={hintStyle}>{selectedPolicy.description}</div>}
          </div>

          {isEditMode && wasPopup && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '14px', cursor: 'pointer', fontSize: '13px', color: 'var(--color-gray-700)' }}>
              <input
                type="checkbox"
                checked={value.bumpRevision}
                onChange={(e) => set('bumpRevision', e.target.checked)}
                style={{ width: '16px', height: '16px', marginTop: '1px', cursor: 'pointer' }}
              />
              <span>
                저장 시 이미 닫은 사용자에게도 다시 표시
                <span style={{ display: 'block', ...hintStyle, marginTop: '2px' }}>
                  체크하지 않으면 기존 사용자별 닫기/숨김 상태가 유지됩니다.
                </span>
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
